import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  TextField, 
  IconButton, 
  styled, 
  useTheme,
  CircularProgress,
  Tooltip,
  Chip
} from '@mui/material';
import { Send, ArrowBack, FiberManualRecord, InfoOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { BASE_URL, XC_TOKEN, TABLES } from '../api/apiConstants';
import axios from 'axios';

const ChatContainer = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 100px)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  boxSizing: 'border-box',
  overflow: 'hidden',
}));

const MessageBubble = styled(Box)(({ theme, sender }) => ({
  maxWidth: '75%',
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  borderRadius: sender === 'AI' 
    ? `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 4px`
    : `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 4px ${theme.shape.borderRadius}px`,
  backgroundColor: sender === 'AI' 
    ? theme.palette.background.paper 
    : theme.palette.primary.main,
  color: sender === 'AI' ? theme.palette.text.primary : theme.palette.common.white,
  position: 'relative',
  alignSelf: sender === 'AI' ? 'flex-start' : 'flex-end',
  '&:before': {
    content: '""',
    position: 'absolute',
    width: '10px',
    height: '10px',
    bottom: '-5px',
    left: sender === 'AI' ? '10px' : 'auto',
    right: sender === 'user' ? '10px' : 'auto',
    transform: 'rotate(45deg)',
    backgroundColor: sender === 'AI' 
      ? theme.palette.background.paper 
      : theme.palette.primary.main,
  }
}));

export default function ChatInterface() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { chatId } = useParams();
  const [currentChat, setCurrentChat] = useState(null);
  const [currentKB, setCurrentKB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        if (chatId) {
          const chatResponse = await axios.get(
            `${BASE_URL}/tables/${TABLES.CHATS}/records/${chatId}`,
            { 
              headers: { "xc-token": XC_TOKEN },
              params: {
                fields: ['recId', 'Name', 'Desc', 'know_recId']
              }
            }
          );

          const chatData = chatResponse.data;
          setCurrentChat({
            recId: chatData.recId,
            name: chatData.Name,
            description: chatData.Desc,
            knowledgeBaseRecId: chatData.know_recId?.[0] || null
          });

          if (chatData.know_recId?.[0]) {
            const kbResponse = await axios.get(
              `${BASE_URL}/tables/${TABLES.KNOWLEDGE}/records/${chatData.know_recId[0]}`,
              { 
                headers: { "xc-token": XC_TOKEN },
                params: {
                  fields: ['recId', 'Name']
                }
              }
            );
            setCurrentKB({
              recId: kbResponse.data.recId,
              name: kbResponse.data.Name
            });
          }
        }
      } catch (error) {
        console.error("Loading error:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [chatId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const messagePayload = {
      text: newMessage,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages(prevMessages => [...prevMessages, messagePayload]);
    setNewMessage('');

    try {
      // Your message processing logic here
    } catch (error) {
      console.error("Error processing message:", error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ChatContainer>
      <Box sx={{ 
        height: '60px',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        px: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton sx={{ color: theme.palette.text.primary }} onClick={() => navigate('/dashboard/chats')}>
            <ArrowBack />
          </IconButton>
          
          <Avatar sx={{ 
            width: 36, 
            height: 36,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontWeight: 600,
            fontSize: '1rem'
          }}>
            {currentChat ? currentChat.name[0].toUpperCase() : 'AI'}
          </Avatar>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ 
              color: theme.palette.text.primary,
              fontWeight: 600
            }}>
              {currentChat ? currentChat.name : 'New Chat'}
            </Typography>
            <FiberManualRecord sx={{ 
              fontSize: 12, 
              color: theme.palette.success.main,
            }} />
          </Box>
        </Box>

        <Tooltip title={currentKB ? `Using: ${currentKB.name}` : 'No knowledge base selected'}>
          <Chip
            icon={<InfoOutlined sx={{ 
              fontSize: 16,
              color: currentKB ? theme.palette.primary.main : theme.palette.text.disabled 
            }} />}
            label={currentKB ? currentKB.name : 'No KB'}
            sx={{
              height: 32,
              px: 1.5,
              bgcolor: currentKB ? theme.palette.primary.light : theme.palette.grey[200],
              color: currentKB ? theme.palette.primary.dark : theme.palette.text.disabled,
              '& .MuiChip-label': {
                fontWeight: 500,
                fontSize: '0.85rem',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }
            }}
          />
        </Tooltip>
      </Box>

      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        py: 2,
        px: 3,
        bgcolor: 'background.default',
      }}>
        {messages.length === 0 ? (
          <MessageBubble sender="AI">
            <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
              Hello! How can I assist you today?
            </Typography>
            <Typography variant="caption" sx={{ 
              display: 'block',
              mt: 0.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              fontSize: '0.7rem'
            }}>
              {new Date().toLocaleTimeString()}
            </Typography>
          </MessageBubble>
        ) : (
          messages.map((msg, index) => (
            <MessageBubble key={index} sender={msg.sender}>
              <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                {msg.text}
              </Typography>
              <Typography variant="caption" sx={{ 
                display: 'block',
                mt: 0.5,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: '0.7rem'
              }}>
                {new Date(msg.timestamp).toLocaleTimeString()}
              </Typography>
            </MessageBubble>
          ))
        )}
      </Box>

      <Box sx={{ 
        height: '70px',
        display: 'flex', 
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 -1px 3px rgba(0,0,0,0.1)',
        boxSizing: 'border-box'
      }}>
        <TextField
          fullWidth
          placeholder="Type your message here..."
          variant="outlined"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSendMessage();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '44px',
              borderRadius: 2,
              boxSizing: 'border-box',
              fontSize: '0.9rem',
              '& fieldset': {
                borderColor: theme.palette.divider,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.light,
              },
            }
          }}
        />
        <IconButton
          onClick={handleSendMessage}
          sx={{
            minWidth: 44,
            minHeight: 44,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.common.white,
            borderRadius: 2,
            '&:hover': {
              bgcolor: theme.palette.primary.dark
            }
          }}
        >
          <Send sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    </ChatContainer>
  );
}